import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/context/AuthContext";

const PREFIX = "cx_draft:";
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

const keyFor = (userId, formId) => `${PREFIX}${userId}:${formId}`;

/** Drafts older than 24 hours are dropped on load — a stale draft is never offered. */
export const purgeStaleDrafts = () => {
  const now = Date.now();
  Object.keys(localStorage)
    .filter((k) => k.startsWith(PREFIX))
    .forEach((k) => {
      try {
        const d = JSON.parse(localStorage.getItem(k));
        if (!d?.saved_at || now - new Date(d.saved_at).getTime() > MAX_AGE_MS) localStorage.removeItem(k);
      } catch (e) {
        localStorage.removeItem(k);
      }
    });
};

export const readDraft = (userId, formId) => {
  try {
    const raw = localStorage.getItem(keyFor(userId, formId));
    if (!raw) return null;
    const d = JSON.parse(raw);
    if (Date.now() - new Date(d.saved_at).getTime() > MAX_AGE_MS) {
      localStorage.removeItem(keyFor(userId, formId));
      return null;
    }
    return d;
  } catch (e) {
    return null;
  }
};

export const writeDraft = (userId, formId, values) => {
  const safe = { ...values };
  Object.keys(safe).forEach((k) => {
    if (/password|passwd|secret|otp/i.test(k)) delete safe[k];
  });
  localStorage.setItem(
    keyFor(userId, formId),
    JSON.stringify({
      form_id: formId,
      user_id: userId,
      path: window.location.pathname,
      saved_at: new Date().toISOString(),
      values: safe,
    })
  );
};

export const clearDraft = (userId, formId) => localStorage.removeItem(keyFor(userId, formId));

// Every mounted form registers a flush so a reload (e.g. the update toast) can save first.
const flushers = new Set();
export const flushAllDrafts = () => flushers.forEach((fn) => { try { fn(); } catch (e) { /* keep going */ } });

export const minutesAgo = (iso) => {
  const mins = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (mins < 1) return "moments ago";
  if (mins === 1) return "1 minute ago";
  if (mins < 60) return `${mins} minutes ago`;
  const hrs = Math.round(mins / 60);
  return hrs === 1 ? "about an hour ago" : `about ${hrs} hours ago`;
};

/**
 * Autosaves a single-object form state and offers to restore it after a reload.
 * Never restores or discards on its own — the user always decides.
 */
export function useFormDraft({ formId, form, setForm, blank, onRestore }) {
  const { user } = useAuth();
  const uid = user?.id;
  const [offer, setOffer] = useState(null);
  const [restored, setRestored] = useState(false);
  const blankRef = useRef(blank);
  blankRef.current = blank;

  const dirty = useMemo(() => {
    if (!form) return false;
    return Object.keys(form).some((k) => JSON.stringify(form[k]) !== JSON.stringify(blankRef.current?.[k]));
  }, [form]);

  useEffect(() => {
    if (!uid) return;
    purgeStaleDrafts();
    const d = readDraft(uid, formId);
    // A draft saved on a different page is left alone until it expires.
    if (d && d.path === window.location.pathname) setOffer(d);
  }, [uid, formId]);

  // Autosave, but never while a restore offer is on screen (it would overwrite the draft with a blank form).
  useEffect(() => {
    if (!uid || !dirty || offer) return;
    const t = setTimeout(() => writeDraft(uid, formId, form), 600);
    return () => clearTimeout(t);
  }, [uid, formId, form, dirty, offer]);

  useEffect(() => {
    if (!uid) return;
    const flush = () => {
      if (dirty && !offer) writeDraft(uid, formId, form);
    };
    flushers.add(flush);
    window.addEventListener("beforeunload", flush);
    return () => {
      flushers.delete(flush);
      window.removeEventListener("beforeunload", flush);
    };
  }, [uid, formId, form, dirty, offer]);

  const restore = useCallback(() => {
    setForm({ ...blankRef.current, ...offer.values });
    setRestored(true);
    setOffer(null);
    onRestore?.();
  }, [offer, setForm, onRestore]);

  const discard = useCallback(() => {
    clearDraft(uid, formId);
    setOffer(null);
  }, [uid, formId]);

  /** Call after a successful submit so the saved draft disappears immediately. */
  const clear = useCallback(() => {
    clearDraft(uid, formId);
    setRestored(false);
  }, [uid, formId]);

  return { offer, restore, discard, restored, clear };
}
